import { Button } from 'components/Button';
import { joinPartnerWorkingHours } from 'helpers/partner.helpers';
import React, { useMemo, useState } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { IPartnerCreate, WeekDays } from 'types/Partner';

import TouchRipple from '@material-ui/core/ButtonBase/TouchRipple';
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select as MaterialSelect,
  styled,
  Table,
  TableBody,
  TableCell as MaterialTableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { Box } from '@mui/system';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';

import { OpeningHoursRow } from './OpeningHoursRow';

const TableCell = styled(MaterialTableCell)(({ theme }) => ({
  borderBottom: 'none',
}));

const Select = styled(MaterialSelect)(({ theme }) => ({
  '& .Mui-disabled': {
    color: theme.palette.text.primary,
    textFillColor: theme.palette.text.primary,
  },
}));

export const OpeningHoursForm: React.FC = () => {
  const { setValue, getValues, resetField, trigger } =
    useFormContext<IPartnerCreate>();
  const [isOpen, setIsOpen] = useState(false);

  const handleConfirm = async () => {
    const newWorkingHours = getValues().workingHoursUpdate;

    if (!newWorkingHours) {
      return;
    }

    try {
      const isValid = await trigger('workingHoursUpdate');

      if (!isValid) {
        return;
      }

      setValue('workingHours', newWorkingHours, { shouldValidate: true });
      setIsOpen(false);
    } catch (reason) {}
  };

  const handleOpen = () => {
    setValue('workingHoursUpdate', getValues().workingHours);
    setIsOpen(true);
  };

  const handleClose = () => {
    setIsOpen(false);
    resetField('workingHoursUpdate');
  };

  const handleApplyToAll = () => {
    const workingHour = getValues().workingHoursUpdate?.monday;

    setValue('workingHoursUpdate', {
      monday: workingHour,
      tuesday: workingHour,
      wednesday: workingHour,
      thursday: workingHour,
      friday: workingHour,
      saturday: workingHour,
      sunday: workingHour,
    });
  };

  return (
    <>
      <Dialog open={isOpen} onClose={handleClose} maxWidth="xl">
        <DialogTitle>Horário de Funcionamento</DialogTitle>
        <Divider />
        <DialogContent>
          <Box>
            <TableContainer>
              <Table sx={{ minWidth: 700 }} aria-label="customized table">
                <TableHead>
                  <TableRow>
                    <TableCell />
                    <TableCell />

                    <TableCell align="left">
                      <Typography fontWeight="bold">De</Typography>
                    </TableCell>

                    <TableCell align="left">
                      <Typography fontWeight="bold"> Até</Typography>
                    </TableCell>

                    <TableCell align="left">
                      <Typography fontWeight="bold"> Intervalo</Typography>
                    </TableCell>

                    <TableCell />
                    <TableCell />
                  </TableRow>
                </TableHead>

                <TableBody>
                  <LocalizationProvider dateAdapter={AdapterDateFns}>
                    {Object.entries(WeekDays).map(([day, title], index) => (
                      <OpeningHoursRow key={day} day={day} title={title}>
                        {index === 0 && (
                          <TableCell sx={{ verticalAlign: 'top' }}>
                            <Button onClick={handleApplyToAll}>
                              APLICAR A TODOS OS DIAS
                            </Button>
                          </TableCell>
                        )}
                      </OpeningHoursRow>
                    ))}
                  </LocalizationProvider>
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        </DialogContent>

        <DialogActions sx={{ m: 4 }}>
          <Button onClick={handleClose}>Cancelar</Button>
          <Button onClick={handleConfirm}>Confirmar</Button>
        </DialogActions>
      </Dialog>

      <OpeningHoursInput onClick={handleOpen} />
    </>
  );
};

const OpeningHoursInput: React.FC<{ onClick: () => void }> = ({ onClick }) => {
  const { control, formState } = useFormContext<IPartnerCreate>();

  const workingHours = useWatch({ control, name: 'workingHours' });
  const error = formState.errors.workingHours?.message;

  const valuePreview = useMemo(
    () => joinPartnerWorkingHours(workingHours),
    [workingHours]
  );

  return (
    <FormControl fullWidth error={Boolean(error)}>
      <InputLabel id="working-hours-label">HORÁRIO DE FUNCIONAMENTO</InputLabel>

      <Select
        labelId="working-hours-label"
        label="HORÁRIO DE FUNCIONAMENTO"
        // disabled
        value={valuePreview}
        inputProps={{ readOnly: true }}
      >
        <MenuItem value={valuePreview}>{valuePreview}</MenuItem>
      </Select>

      <TouchRipple
        onClick={onClick}
        style={{
          position: 'absolute',
          pointerEvents: 'all',
          cursor: 'pointer',
        }}
      />

      <FormHelperText>{error}</FormHelperText>
    </FormControl>
  );
};

export default OpeningHoursForm;

